import { Close } from '@icon-park/react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { Justification } from '@shared/models'

import { Button, TableImage } from '@/components'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { sanitizePublicationHtml } from '@/utils/strings'

import { useMentionsContext } from '../../Mentions.context'
import { appendAtIfNeeded } from '../../utils'
import {
  Container,
  ContentColumn,
  ContentSection,
  Header,
  JustificationBanner,
  JustificationLabel,
  JustificationText,
  JustificationTextArea,
  LoadingText,
  LogosColumn,
  MentionContent,
  Overlay,
  PublicationText,
  PublisherName,
  TabButton,
  TabContainer,
  TabContent,
  Title,
  Footer,
} from './MentionDetailModal.styles'

type TabType = 'POLARITY_INTENSITY' | 'ROLE' | 'THEMES' | 'KEY_MESSAGE'

const TAB_LABELS: Record<TabType, string> = {
  POLARITY_INTENSITY: 'Favorabilidade',
  ROLE: 'Abordagem',
  THEMES: 'Tema',
  KEY_MESSAGE: 'Mensagem-chave',
}

export function MentionDetailModal(): JSX.Element | null {
  const { companyID } = useRouting()
  const { currentMention: currMention, selectMention } = useMentionsContext()
  const currentMention = currMention?.val

  const { sendAlert } = Alert.Hook()

  const [justifications, setJustifications] = useState<Justification[]>([])
  const [activeTab, setActiveTab] = useState<TabType>('POLARITY_INTENSITY')
  const [isLoading, setIsLoading] = useState(false)

  const [fetchJustifications] = Api.PromiseQuery<Justification[]>(
    `companies/${companyID}/mentions/${`${currentMention?.postID}_${currentMention?.brandID}`}/justifications`,
  )

  useEffect(() => {
    if (currentMention?.postID) {
      loadJustifications()
    }
  }, [currentMention?.postID])

  const loadJustifications = async () => {
    if (!currentMention?.postID) return

    setIsLoading(true)
    try {
      const [data, error] = await fetchJustifications({})

      if (error) {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao carregar justificativas',
        })
        return
      }

      if (data) {
        setJustifications(data)
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao carregar justificativas',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    selectMention(undefined)
  }

  const getJustificationForTab = (tab: TabType): string => {
    const justification = justifications.find((j) => j.promptType === tab)
    return justification?.justification || 'Nenhuma justificativa disponível'
  }

  if (!currentMention) return null

  const mainJustification = justifications.find((j) => j.promptType === 'POLARITY_INTENSITY')

  return createPortal(
    <Overlay onClick={handleClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Justificativas dadas pela IA no processo de anotação</Title>
          <Button.Root $variant="link" onClick={handleClose} aria-label="Fechar">
            <Button.Icon value={Close} size={20} />
          </Button.Root>
        </Header>

        <ContentSection>
          {mainJustification && (
            <JustificationBanner>
              <JustificationLabel>Justificativa Principal:</JustificationLabel>
              <JustificationText>{mainJustification.justification}</JustificationText>
            </JustificationBanner>
          )}

          <MentionContent>
            <LogosColumn>
              <TableImage id={currentMention.channelLogoID} hyperlink={currentMention.hyperlink} />
              <TableImage id={currentMention.brandLogo} />
            </LogosColumn>
            <ContentColumn>
              <PublisherName>{appendAtIfNeeded(currentMention.profile)}</PublisherName>
              <PublicationText
                dangerouslySetInnerHTML={{
                  __html: sanitizePublicationHtml(currentMention.publication || 'Sem conteúdo'),
                }}
              />
            </ContentColumn>
          </MentionContent>
        </ContentSection>

        <TabContainer>
          <TabButton $active={activeTab === 'POLARITY_INTENSITY'} onClick={() => setActiveTab('POLARITY_INTENSITY')}>
            {TAB_LABELS['POLARITY_INTENSITY']}
          </TabButton>
          <TabButton $active={activeTab === 'ROLE'} onClick={() => setActiveTab('ROLE')}>
            {TAB_LABELS['ROLE']}
          </TabButton>
          <TabButton $active={activeTab === 'THEMES'} onClick={() => setActiveTab('THEMES')}>
            {TAB_LABELS['THEMES']}
          </TabButton>
          <TabButton $active={activeTab === 'KEY_MESSAGE'} onClick={() => setActiveTab('KEY_MESSAGE')}>
            {TAB_LABELS['KEY_MESSAGE']}
          </TabButton>
        </TabContainer>

        <TabContent>
          {isLoading ?
            <LoadingText>Carregando justificativas...</LoadingText>
          : <JustificationTextArea
              value={getJustificationForTab(activeTab)}
              readOnly
              placeholder="Nenhuma justificativa disponível"
            />
          }
        </TabContent>

        <Footer>
          <Button.Root $variant="secondary" onClick={handleClose}>
            <Button.Text value="Fechar" />
          </Button.Root>
        </Footer>
      </Container>
    </Overlay>,
    document.body,
  )
}
